<template>
  <div class="container v-content-container tooltip-wrapper popup-wrapper">
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-user"></i>
          <span v-if="selectedCustomerId != null">
            {{
              noDataExist
                ? "Customer"
                : beautifyName(originalFirstName, originalLastName)
            }}
            <span v-show="customerDeleted">(gelöscht)</span>
          </span>
          <span v-else-if="!loading">
            {{ $label("customer.new_customer", "New customer") }}
          </span>
          <customer-actions-desktop
            :customer-id="this.selectedCustomerId"
          ></customer-actions-desktop>
        </h1>
      </div>
    </div>

    <navigation-tabs :customer-id="selectedCustomerId"></navigation-tabs>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <customer-actions-mobile
        :customer-id="selectedCustomerId"
      ></customer-actions-mobile>

      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div v-show="!loading && customerBalance > 0" class="row">
        <div class="col-xs-12">
          <alert-box type="warning" :closeable="true" :show-icon="true">
            {{
              $label(
                "customer.alert_balance",
                "This customer has a balance of "
              )
            }}
            xx.
          </alert-box>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>

      <div v-show="!loading && customerLocked" class="row">
        <div class="col-xs-12">
          <alert-box type="danger" :closeable="false" :show-icon="true">
            {{
              $label(
                "customer.alert_locked",
                "This customer is currently locked."
              )
            }}
          </alert-box>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>

      <div v-show="!loading && customerUnconfirmed" class="row">
        <div class="col-xs-12 text-center">
          <alert-box type="info" :closeable="true" :show-icon="true">
            {{
              $label(
                "customer.alert_not_confirmed",
                "This customer is not yet confirmed."
              )
            }}
          </alert-box>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>

      <div v-show="!loading && customerInterested" class="row">
        <div class="col-xs-12 text-center">
          <alert-box type="info" :closeable="true" :show-icon="true">
            {{
              $label(
                "customer.alert_customer_interested",
                "This customer is listed as interested: The customer has registered online without booking anything so far."
              )
            }}
          </alert-box>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>

      <div v-show="!loading && exceedanceWarning" lass="row">
        <div class="col-xs-12">
          <alert-box type="danger" :closeable="false" :show-icon="true">
            {{
              $label("customer.alert_exceedance_warning", "Exceedance warning.")
            }}
          </alert-box>
        </div>
      </div>

      <!-- Tab panes -->
      <div v-show="!loading" class="tab-content tooltip-wrapper">
        <div id="form-customer-data">
          <div class="tab-pane active" id="customer-data">
            <div class="row">
              <div class="col-sm-6">
                <h2>
                  <i class="fa fa-fw fa-lg fa-user"></i>
                  <span id="customer-header">
                    {{ $label("customer.customer", "Customer") }}</span
                  >
                </h2>
                <membership-logo
                  :membership-status="membershipStatus"
                ></membership-logo>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <div class="row">
                        <!-- BEGIN Gender -->
                        <section class="col col-6">
                          <label class="label">{{
                            $label("common.gender", "Gender")
                          }}</label>
                          <label class="select">
                            <select
                              v-model="selectedCustomer.gender"
                              :disabled="updating"
                            >
                              <option value="0">{{
                                $label("common.gender", "Gender")
                              }}</option>
                              <option value="1">{{
                                $label("common.gender_female", "Female")
                              }}</option>
                              <option value="2">{{
                                $label("common.gender_male", "Male")
                              }}</option>
                            </select>
                            <i></i>
                          </label>
                        </section>
                        <!-- End Gender -->

                        <!-- BEGIN Title -->
                        <section class="col col-6">
                          <text-input
                            v-model="selectedCustomer.title"
                            id="customer-title"
                            :label="$label('customer.title', 'Title')"
                            :placeholder="$label('customer.title', 'Title')"
                            :has-error="hasError('title')"
                            :error="getError('title')"
                            :is-disabled="updating"
                            @keyup.native.enter="createOrUpdateCustomer"
                          />
                        </section>
                        <!-- END Title -->
                      </div>
                      <div class="row">
                        <!-- BEGIN Last name -->
                        <section class="col col-6">
                          <text-input
                            v-model="selectedCustomer.last_name"
                            id="customer-last-name"
                            :label="$label('common.last_name', 'Last name')"
                            :placeholder="
                              $label('common.last_name', 'Last name')
                            "
                            :has-error="hasError('last_name')"
                            :error="getError('last_name')"
                            :is-disabled="updating"
                            @keyup.native.enter="createOrUpdateCustomer"
                          />
                        </section>
                        <!-- END Last name -->

                        <!-- BEGIN First name -->
                        <section class="col col-6">
                          <text-input
                            v-model="selectedCustomer.first_name"
                            id="customer-first-name"
                            :label="$label('common.first_name', 'First name')"
                            :placeholder="
                              $label('common.first_name', 'First name')
                            "
                            :has-error="hasError('first_name')"
                            :error="getError('first_name')"
                            :is-disabled="updating"
                            @keyup.native.enter="createOrUpdateCustomer"
                          />
                        </section>
                        <!-- END First name -->
                      </div>

                      <!-- BEGIN Email -->
                      <section>
                        <label class="label">{{
                          $label("common.email_address_long", "E-Mail address")
                        }}</label>
                        <label class="input">
                          <i class="icon-append icon-envelope"></i>
                          <input
                            v-model="selectedCustomer.email"
                            :class="{ 'text-input-error': hasError('email') }"
                            :placeholder="$label('common.email_address_short')"
                            :disabled="updating"
                            id="customer-email"
                            type="email"
                          />
                        </label>

                        <p v-if="hasError('email')" class="text-danger">
                          {{ getError("email") }}
                        </p>
                      </section>
                      <!-- END Email -->

                      <div class="row">
                        <!-- BEGIN Mobile phone country code -->
                        <section class="col col-3">
                          <label class="label">{{
                            $label("common.prefix", "Prefix")
                          }}</label>
                          <label class="input">
                            <input
                              :class="{
                                'text-input-error': hasError(
                                  'phone_number.mobile_prefix'
                                )
                              }"
                              type="text"
                              placeholder="XX"
                              :disabled="updating"
                              id="customer-mobile-phone-prefix"
                            />
                          </label>

                          <p
                            v-if="hasError('phone_number.mobile_prefix')"
                            class="text-danger"
                          >
                            {{ getError("phone_number.mobile_prefix") }}
                          </p>
                        </section>
                        <!-- END Mobile phone country code -->

                        <!-- BEGIN Mobile phone number -->
                        <section class="col col-9">
                          <label class="label">{{
                            $label(
                              "common.mobile_phone_number",
                              "Mobile phone number"
                            )
                          }}</label>
                          <label class="input">
                            <i class="icon-append fa fa-mobile-phone"></i>
                            <input
                              :class="{
                                'text-input-error': hasError(
                                  'phone_number.mobile_number'
                                )
                              }"
                              :placeholder="
                                $label(
                                  'common.phone_number_placeholder',
                                  'XXXXXXXX'
                                )
                              "
                              :disabled="updating"
                              id="customer-mobile-phone-number"
                              type="tel"
                            />
                          </label>

                          <p
                            v-if="hasError('phone_number.mobile_number')"
                            class="text-danger"
                          >
                            {{ getError("phone_number.mobile_number") }}
                          </p>
                        </section>
                        <!-- END Mobile phone number -->
                      </div>

                      <div class="row">
                        <!-- BEGIN Phone country code -->
                        <section class="col col-3">
                          <label class="label">{{
                            $label("common.prefix", "Prefix")
                          }}</label>
                          <label class="input">
                            <input
                              v-model="selectedCustomer.phone_number.prefix"
                              :class="{
                                'text-input-error': hasError(
                                  'phone_number.prefix'
                                )
                              }"
                              type="text"
                              placeholder="XX"
                              :disabled="updating"
                              id="customer-phone-prefix"
                            />
                          </label>

                          <p
                            v-if="hasError('phone_number.prefix')"
                            class="text-danger"
                          >
                            {{ getError("phone_number.prefix") }}
                          </p>
                        </section>
                        <!-- END Phone country code -->

                        <!-- BEGIN Phone number -->
                        <section class="col col-9">
                          <label class="label">{{
                            $label("common.phone_number", "Phone number")
                          }}</label>
                          <label class="input">
                            <i class="icon-append fa fa-phone"></i>
                            <input
                              v-model="selectedCustomer.phone_number.number"
                              :class="{
                                'text-input-error': hasError(
                                  'phone_number.number'
                                )
                              }"
                              :placeholder="
                                $label(
                                  'common.phone_number_placeholder',
                                  'XXXXXXXX'
                                )
                              "
                              :disabled="updating"
                              id="customer-phone-number"
                              type="tel"
                            />
                          </label>

                          <p
                            v-if="hasError('phone_number.number')"
                            class="text-danger"
                          >
                            {{ getError("phone_number.number") }}
                          </p>
                        </section>
                        <!-- END Phone number -->
                      </div>

                      <div class="row">
                        <!-- BEGIN Position -->
                        <section class="col col-6">
                          <text-input
                            v-model="selectedCustomer.position"
                            id="customer-position"
                            :label="$label('customer.position', 'Position')"
                            :placeholder="
                              $label('customer.position', 'Position | Job')
                            "
                            :has-error="hasError('position')"
                            :error="getError('position')"
                            :is-disabled="updating"
                            @keyup.native.enter="createOrUpdateCustomer"
                          />
                        </section>
                        <!-- END Position -->

                        <!-- BEGIN Birthdate -->
                        <section class="col col-6">
                          <label class="label">{{
                            $label("common.date_of_birth", "Date of birth")
                          }}</label>
                          <date-picker
                            v-model="selectedCustomer.birthdate"
                            :format="dateFormat"
                            :readonly="updating"
                            :disabled="updating"
                            class="input"
                            id="customer-birthdate"
                            :class="{
                              'text-input-error': hasError('birthdate')
                            }"
                          >
                          </date-picker>

                          <p v-if="hasError('birthdate')" class="text-danger">
                            {{ getError("birthdate") }}
                          </p>
                        </section>
                        <!-- END Birthdate -->
                      </div>

                      <!-- BEGIN Internal note -->
                      <section>
                        <label class="label">{{
                          $label("customer.note", "Internal note")
                        }}</label>
                        <label class="textarea">
                          <textarea
                            v-model="selectedCustomer.note"
                            :class="{ 'text-input-error': hasError('note') }"
                            :placeholder="$label('customer.note', 'Note')"
                            :disabled="updating"
                            id="customer-note"
                            rows="3"
                          >
                          </textarea>
                        </label>
                      </section>
                      <!-- END Internal note -->
                    </fieldset>
                  </div>
                </div>
              </div>

              <div class="col-sm-6">
                <h2>
                  <i class="fa fa-fw fa-lg fa-home"></i>
                  {{ $label("common.address", "Address") }}
                </h2>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <div class="row">
                        <!-- BEGIN Country -->
                        <section class="col col-6">
                          <label class="label">{{
                            $label("common.country")
                          }}</label>

                          <label class="select">
                            <select
                              v-model="selectedCustomer.address.country_id"
                              @change="onChangeSelectedCountry($event)"
                              id="customer-address-country"
                              :disabled="updating"
                            >
                              <option
                                v-for="country in countries"
                                :value="country.id"
                                :id="'customer-address-country-' + country.id"
                                :selected="countryShouldBeSelected(country)"
                                v-bind:key="country.id"
                                >{{ $label("common.country_" + country.slug) }}
                              </option>
                            </select>
                            <i></i>
                          </label>

                          <p
                            v-if="hasError('address.country_id')"
                            class="text-danger"
                          >
                            {{ getError("address.country_id") }}
                          </p>
                        </section>
                        <!-- END Country -->

                        <!-- BEGIN Country states -->
                        <section class="col col-6">
                          <label class="label">{{
                            $label("common.country_states", "States")
                          }}</label>

                          <label class="select">
                            <select
                              v-model="selectedCustomer.address.state_id"
                              :disabled="selectedCountry.states == null"
                              id="customer-address-state"
                            >
                              <option
                                v-for="state in selectedCountry.states"
                                :value="state.id"
                                :id="'customer-address-state-' + state.id"
                                :selected="stateShouldBeSelected(state)"
                                v-bind:key="state.id"
                                >{{
                                  $label(
                                    "common.state_" + state.slug,
                                    state.name
                                  )
                                }}
                              </option>
                            </select>
                            <i></i>
                          </label>

                          <p
                            v-if="hasError('address.state_id')"
                            class="text-danger"
                          >
                            {{ getError("address.state_id") }}
                          </p>
                        </section>
                        <!-- END Country states -->
                      </div>

                      <div class="row">
                        <!-- BEGIN Address city -->
                        <section class="col col-8">
                          <text-input
                            v-model="selectedCustomer.address.city"
                            id="customer-address-city"
                            :label="$label('common.city', 'City')"
                            :placeholder="$label('common.city', 'City')"
                            :has-error="hasError('address.city')"
                            :error="getError('address.city')"
                            :is-disabled="updating"
                            @keyup.native.enter="createOrUpdateCustomer"
                          />
                        </section>
                        <!-- END Address city -->

                        <!-- BEGIN Post code -->
                        <section class="col col-4">
                          <text-input
                            v-model="selectedCustomer.address.post_code"
                            id="customer-address-post-code"
                            :label="$label('common.post_code', 'Post code')"
                            :placeholder="
                              $label('common.post_code', 'Post code')
                            "
                            :has-error="hasError('address.post_code')"
                            :error="getError('address.post_code')"
                            :is-disabled="updating"
                            @keyup.native.enter="createOrUpdateCustomer"
                          />
                        </section>
                        <!-- END Post code -->
                      </div>
                      <div class="row">
                        <!-- BEGIN Street name -->
                        <section class="col col-8">
                          <text-input
                            v-model="selectedCustomer.address.street_name"
                            id="customer-address-street-name"
                            :label="$label('common.street_name', 'Street')"
                            :placeholder="
                              $label('common.street_name', 'Street')
                            "
                            :has-error="hasError('address.street_name')"
                            :error="getError('address.street_name')"
                            :is-disabled="updating"
                            @keyup.native.enter="createOrUpdateCustomer"
                          />
                        </section>
                        <!-- END Street name -->

                        <!-- BEGIN Street number -->
                        <section class="col col-4">
                          <text-input
                            v-model="selectedCustomer.address.street_number"
                            id="customer-address-street-number"
                            :label="
                              $label('common.street_number', 'Street number')
                            "
                            :placeholder="
                              $label('common.street_number', 'Street number')
                            "
                            :has-error="hasError('address.street_number')"
                            :error="getError('address.street_number')"
                            :is-disabled="updating"
                            @keyup.native.enter="createOrUpdateCustomer"
                          />
                        </section>
                        <!-- END Street number -->
                      </div>

                      <!-- BEGIN Address info -->
                      <section>
                        <label class="label">{{
                          $label(
                            "common.address_additional_info",
                            "Additional info"
                          )
                        }}</label>
                        <label class="textarea">
                          <textarea
                            v-model="selectedCustomer.address.additional_info"
                            :class="{
                              'text-input-error': hasError(
                                'address.additional_info'
                              )
                            }"
                            :placeholder="
                              $label(
                                'common.address_additional_info',
                                'Additional info'
                              )
                            "
                            :disabled="updating"
                            id="customer-address-additional-info"
                            rows="3"
                          >
                          </textarea>
                        </label>
                      </section>
                      <!-- END Address info -->

                      <div class="row" v-show="addressCheckActive">
                        <section class="col col-6" v-show="isPortalUser">
                          <label class="label">Address-Prüfung</label>
                          <label
                            v-if="addressVerified"
                            class="note text-v-green-override"
                            >Adresse bestätigt</label
                          >
                          <label v-else class="note text-v-orange-override"
                            >Adresse NICHT bestätigt</label
                          >
                        </section>
                        <section class="col col-6" v-show="isPortalUser">
                          <label class="label">&nbsp;</label>
                          <a
                            v-if="addressVerified"
                            style="color: white !important;"
                            class="btn btn-primary v-form-button disabled"
                          >
                            <i class="fa fa-fw fa-thumbs-o-up"></i> Jetzt
                            bestätigen
                          </a>
                          <a
                            v-else
                            href="/customers/customerId/addresscheck/confirm/"
                            style="color: white !important;"
                            class="btn btn-primary v-form-button"
                          >
                            <i class="fa fa-fw fa-thumbs-o-up"></i> Jetzt
                            bestätigen
                          </a>
                        </section>
                        <section
                          class="col col-12"
                          v-show="!isPortalUser && selectedCustomerId != null"
                        >
                          <label class="label">Address-Prüfung</label>
                          <label class="note"
                            >Kunde hat sich online noch nicht angemeldet</label
                          >
                        </section>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-sm-6 tooltip-wrapper">
                <h2>
                  <i class="fa fa-fw fa-lg fa-file-text-o"></i>
                  {{ $label("common.invoice_recipient", "Invoice recipient") }}
                </h2>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <section>
                        <label class="label">
                          Name
                          <a
                            class="pull-right"
                            v-tooltip="{
                              content: 'Aktuelle Rechnungsanschrift',
                              classes: 'custom-tooltip'
                            }"
                            >Aktuelle Rechnungsanschrift</a
                          >
                        </label>
                        <label class="note"
                          >Title Firstname Lastname, Business Name</label
                        >
                      </section>
                      <section>
                        <label class="label">Anschrift</label>
                        <label class="select">
                          <select name="billing_address">
                            <option value="" selected
                              >Street, ZIP City (RecipientAddressCode)></option
                            >
                          </select>
                          <i></i>
                        </label>
                      </section>
                      <section>
                        <label class="label">Empfänger</label>
                        <label class="select">
                          <select name="billing_email">
                            <option value="" selected
                              >Name, Function, Email</option
                            >
                          </select>
                          <i></i>
                        </label>
                      </section>
                      <section>
                        <label class="label"
                          >Rechnung per E-Mail zusätzlicher Empfänger</label
                        >
                        <label class="input">
                          <i class="icon-append icon-envelope"></i>
                          <input
                            value=""
                            type="email"
                            name="additional_billing_email"
                            placeholder="E-Mail Adresse für CC"
                          />
                        </label>
                      </section>
                      <section>
                        <label class="label">Zusatztext für Rechnung</label>
                        <label class="input">
                          <input
                            type="text"
                            name="billing_additional_text"
                            value=""
                          />
                        </label>
                      </section>
                      <section>
                        <label class="label"
                          >Auftragsreferenz-Nummer (Identifikation des Kunden
                          auf Rechnungen)</label
                        >
                        <label class="input">
                          <input
                            name="billing_reference_number"
                            type="text"
                            max-length="255"
                            value=""
                          />
                        </label>
                      </section>

                      <section>
                        <label class="label">Lieferantennummer</label>
                        <label class="input">
                          <input
                            name="billing_biller_number"
                            type="text"
                            max-length="255"
                            value=""
                          />
                        </label>
                      </section>
                      <section>
                        <label class="checkbox">
                          <input
                            type="checkbox"
                            value="on"
                            name="invoice_via_letter"
                          /><i></i> Rechnung per Post
                        </label>
                        <label class="checkbox">
                          <input
                            type="checkbox"
                            value="on"
                            name="disable_customer_billrun"
                          /><i></i> vom Rechnungslauf ausschließen
                        </label>
                      </section>
                    </fieldset>
                  </div>
                </div>
              </div>

              <div class="col-sm-6">
                <div class="row">
                  <div class="col-xs-12">
                    <h2>
                      <i class="fa fa-fw fa-lg fa-bank"></i>
                      {{ $label("customer.bank_details", "Bank details") }}
                    </h2>
                    <p></p>
                    <div class="v-form-partial">
                      <div class="v-form">
                        <fieldset>
                          <div class="row">
                            <section class="col col-6">
                              <label class="label">Kontoinhaber</label>
                              <label class="input">
                                <input
                                  value=""
                                  type="text"
                                  name="customer_account_name"
                                  maxlength="34"
                                />
                              </label>
                            </section>
                            <section class="col col-6">
                              <label class="label">Bankname</label>
                              <label class="input">
                                <input
                                  value=""
                                  type="text"
                                  name="customer_bank"
                                  placeholder="Bankname"
                                />
                              </label>
                            </section>
                          </div>

                          <div class="row">
                            <section class="col col-8">
                              <label class="label">IBAN</label>
                              <label class="input">
                                <input
                                  value=""
                                  type="text"
                                  name="customer_iban"
                                  placeholder="IBAN"
                                  maxlength="34"
                                />
                              </label>
                            </section>
                            <section class="col col-4">
                              <label class="label">BIC</label>
                              <label class="input">
                                <input
                                  value=""
                                  type="text"
                                  name="customer_bic"
                                  placeholder="BIC"
                                  maxlength="11"
                                />
                              </label>
                            </section>
                          </div>
                        </fieldset>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row"><div class="col-xs-12">&nbsp;</div></div>

                <div class="row"><div class="col-xs-12">&nbsp;</div></div>

                <div class="row">
                  <div class="col-xs-12">
                    <h2>
                      <i class="fa fa-fw fa-lg fa-cog"></i>
                      {{ $label("customer.settings", "Settings") }}
                    </h2>
                    <p></p>
                    <div id="operator-bill-address" class="v-form-partial">
                      <div class="v-form">
                        <fieldset>
                          <div class="row">
                            <!-- BEGIN Customer number -->
                            <section class="col col-6">
                              <text-input
                                v-model="selectedCustomer.number"
                                id="customer-number"
                                :label="
                                  $label('customer.number', 'Customer number')
                                "
                                :placeholder="
                                  $label('customer.number', 'Customer number')
                                "
                                :has-error="hasError('number')"
                                :error="getError('number')"
                                :is-disabled="updating"
                                @keyup.native.enter="createOrUpdateCustomer"
                              />
                            </section>
                            <!-- END Customer number -->

                            <section class="col col-4">
                              <label class="label"
                                >{{
                                  $label("customer.color_code", "Color code")
                                }}
                                <a
                                  href="http://www.colorhexa.com"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="pull-right"
                                  ><i class="fa fa-fw fa-external-link"></i
                                ></a>
                              </label>
                              <label class="input">
                                <v-swatches
                                  v-model="selectedCustomer.color_code"
                                  show-fallback
                                  swatches="text-advanced"
                                  popover-x="left"
                                >
                                  <input
                                    v-model="selectedCustomer.color_code"
                                    slot="trigger"
                                    class="form__input__element"
                                    :class="{
                                      'text-input-error': hasError('color_code')
                                    }"
                                    :disabled="updating"
                                    id="customer-color-code"
                                    type="text"
                                  />
                                </v-swatches>
                              </label>

                              <p
                                v-if="hasError('color_code')"
                                class="text-danger"
                              >
                                {{ getError("color_code") }}
                              </p>
                            </section>
                            <section class="col col-2">
                              <label class="label">Preview</label>
                              <label class="input">
                                <input
                                  :style="{
                                    'background-color':
                                      selectedCustomer.color_code +
                                      ' !important'
                                  }"
                                  disabled="disabled"
                                  class="color-preview"
                                />
                              </label>
                            </section>
                          </div>
                          <div class="row">
                            <section class="col col-3">
                              <label class="label">Schrift</label>
                              <label class="select">
                                <select name="customer_font_color">
                                  <option
                                    value="#FFFFFF"
                                    style="background:#FFFFFF;color:black;"
                                    >Hell</option
                                  >
                                  <option
                                    value="#333333"
                                    selected
                                    style="background:#333333;color:white;"
                                    >Dunkel</option
                                  >
                                </select>
                                <i></i>
                              </label>
                            </section>
                            <section class="col col-9">
                              <label class="label">Referenz-ID</label>
                              <label class="input">
                                <input
                                  value=""
                                  type="text"
                                  name="reference_id"
                                />
                              </label>
                            </section>
                          </div>
                          <!-- BEGIN Payment type -->
                          <section>
                            <label class="label font-bold typo__label"
                              >Erlaubte Online-Zahlungsarten für diesen
                              Kunden</label
                            >
                            <multiselect
                              v-model="paymentMethodValue"
                              tag-placeholder=""
                              placeholder="Search or add payment method"
                              label="name"
                              track-by="id"
                              :options="paymentMethodOptions"
                              class="v-multiselect v-form"
                              :multiple="true"
                              :taggable="false"
                              :show-labels="false"
                              @tag="addPaymentMethod"
                            >
                            </multiselect>
                            <div class="selectVenues">
                              <a
                                class="note action-link cursor-pointer"
                                id="to-default-payment-methods"
                                @click="restoreDefaultPaymentMethods"
                                >Standard-Zahlungsarten</a
                              >
                            </div>
                          </section>
                          <!-- END Payment type -->
                        </fieldset>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row"><div class="col-xs-12">&nbsp;</div></div>

            <div class="row">
              <div class="col-sm-6 tooltip-wrapper">
                <h2>
                  <i class="fa fa-fw fa-lg fa-users"></i>
                  {{ $label("customer.groups", "Groups") }}
                </h2>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <!-- BEGIN Customer groups -->
                      <section>
                        <v-multiselect-listbox
                          :options="availableGroups"
                          v-model="selectedGroups"
                          :reduce-display-property="group => group.name"
                          :reduce-value-property="group => group.id"
                          :show-select-all-buttons="false"
                          search-options-placeholder="Search groups"
                          selected-options-placeholder="Search selected groups"
                          no-options-text="No groups"
                          selected-no-options-text="No groups selected"
                          no-options-found-text="No group found"
                          no-selected-options-found-text="No selected group found"
                          search-input-class="custom-input-class"
                          :disabled="false"
                          @change="changeGroups"
                        >
                        </v-multiselect-listbox>
                      </section>
                      <!-- END Customer groups -->
                      <div class="row">
                        <section class="col col-6">
                          <alert-box
                            type="info"
                            :closeable="false"
                            :show-icon="true"
                            :padding="true"
                          >
                            <router-link :to="{ name: 'GroupsOverview' }"
                              ><b class="text-info">{{
                                $label(
                                  "customer.configure_groups",
                                  "Configure groups"
                                )
                              }}</b></router-link
                            >
                          </alert-box>
                        </section>
                        <section
                          class="col col-6"
                          v-show="membershipStatus != null"
                        >
                          <alert-box
                            type="info"
                            :closeable="false"
                            :show-icon="true"
                            :padding="true"
                          >
                            <router-link :to="{ name: 'CustomerMembership' }"
                              ><b class="text-info">{{
                                $label(
                                  "customer.configure_membership",
                                  "Configure membership"
                                )
                              }}</b></router-link
                            >
                          </alert-box>
                        </section>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>

              <div class="col-sm-6">
                <h2>
                  <i class="fa fa-fw fa-lg fa-users"></i>
                  {{ $label("customer.organizations", "Organizations") }}
                </h2>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <!-- BEGIN Organizations -->
                      <section>
                        <v-multiselect-listbox
                          :options="availableOrganizations"
                          v-model="selectedOrganizations"
                          :reduce-display-property="
                            organization => organization.name
                          "
                          :reduce-value-property="
                            organization => organization.id
                          "
                          :show-select-all-buttons="false"
                          search-options-placeholder="Search organizations"
                          selected-options-placeholder="Search selected organizations"
                          no-options-text="No organizations"
                          selected-no-options-text="No groups organizations"
                          no-options-found-text="No organization found"
                          no-selected-options-found-text="No selected organization found"
                          search-input-class="custom-input-class"
                          :disabled="false"
                          @change="changeOrganizations"
                        >
                        </v-multiselect-listbox>
                      </section>
                      <!-- END Organizations -->
                      <div class="row">
                        <section class="col col-6">
                          <alert-box
                            type="info"
                            :closeable="false"
                            :show-icon="true"
                            :padding="true"
                          >
                            <router-link :to="{ name: 'OrganizationsOverview' }"
                              ><b class="text-info">{{
                                $label(
                                  "customer.configure_organizations",
                                  "Configure organizations"
                                )
                              }}</b></router-link
                            >
                          </alert-box>
                        </section>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>
            </div>

            <div class="row"><div class="col-xs-12">&nbsp;</div></div>

            <div class="row">
              <div class="col-sm-6 tooltip-wrapper">
                <h2>
                  <i class="fa fa-fw fa-lg fa-hand-pointer-o"></i>
                  {{
                    $label(
                      "customer.allowed_use_of_customer_data",
                      "Allowed use of customer data"
                    )
                  }}
                </h2>
                <p></p>
                <div class="v-form-partial">
                  <div class="v-form">
                    <fieldset>
                      <section>
                        <label class="label">Title</label>
                        <label class="checkbox">
                          <input
                            type="checkbox"
                            class="allowance-checkbox"
                            value="true"
                            name="personaldata_code"
                            checked
                          /><i></i>&nbsp; erlaubt am d.m.Y durch Kunden selbst /
                          durch allowed_by
                        </label>
                        <label class="note">Description</label>
                        <label class="note"
                          ><a
                            href="/customers/customerId/allowance/code/show/"
                            target="_blank"
                            >Zusätzliche Info</a
                          ></label
                        >
                      </section>
                    </fieldset>
                  </div>
                </div>
              </div>

              <div class="col-sm-6">
                <h2>
                  <i class="fa fa-fw fa-lg fa-edit"></i>
                  {{ $label("customer.editing", "Editing") }}
                </h2>
                <p></p>

                <div class="v-form">
                  <fieldset>
                    <section>
                      <label class="label">Herkunft</label>
                      <label class="input">
                        customerOrigin
                      </label>
                    </section>
                    <div class="row">
                      <section class="col col-6">
                        <label class="label">Erstellt</label>
                        <label class="input">
                          d.m.Y H:i
                        </label>
                      </section>
                      <section class="col col-6">
                        <label class="label">Zuletzt geändert</label>
                        <label class="input">
                          d.m.Y H:i
                        </label>
                      </section>
                    </div>
                    <div class="row">
                      <section class="col col-6">
                        <label class="label">E-Mail Login</label>
                        <label class="input">Accountname</label>
                      </section>
                      <section class="col col-6">
                        <label class="label">Username</label>
                        <label class="input">Account Username</label>
                      </section>
                    </div>
                    <div class="row">
                      <section class="col col-6">
                        <label class="label">E-Mail Login</label>
                        <label class="input">Accountname</label>
                      </section>
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>

            <div class="row"><div class="col-xs-12">&nbsp;</div></div>
          </div>

          <hr />

          <div class="row">
            <div class="col-xs-12 text-center">
              <success-button
                v-show="customerUnconfirmed"
                :class="{ 'is-disabled': updating }"
                id="confirm-customer-button"
                icon="fa fa-fw fa-lg fa-save"
                :text="$label('customer.confirm_customer', 'Confirm customer')"
              >
              </success-button>
              &nbsp;
              <info-button
                :class="{ 'is-disabled': updating }"
                id="save-customer-button"
                icon="fa fa-fw fa-lg fa-save"
                :text="$label('common.save', 'Save')"
                @click.native="createOrUpdateCustomer"
              >
              </info-button>
              &nbsp;
              <danger-button
                :class="{
                  'is-disabled': updating || selectedCustomerId == null
                }"
                id="delete-customer-button"
                icon="fa fa-fw fa-lg fa-trash-o"
                :text="$label('common.delete', 'Delete')"
              >
              </danger-button>
              &nbsp;
              <danger-button
                v-show="!customerLocked"
                :class="{ 'is-disabled': updating }"
                id="lock-customer-button"
                icon="fa fa-fw fa-lg fa-trash-o"
                :text="$label('customer.lock_customer', 'Lock custmoer')"
              >
              </danger-button>
              <warning-button
                v-show="customerLocked"
                :class="{ 'is-disabled': updating }"
                id="unlock-customer-button"
                icon="fa fa-fw fa-lg fa-trash-o"
                :text="$label('customer.unlock_customer', 'Unlock custmoer')"
              >
              </warning-button>
            </div>
          </div>
        </div>
      </div>

      <div class="row"><div class="col-xs-12">&nbsp;</div></div>

      <div class="row mb-4">
        <div class="col-xs-12"></div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api";
import swal from "sweetalert";
import FormErrorsMixin from "@/mixins/FormErrors";

import Multiselect from "vue-multiselect";

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/de";

import VSwatches from "vue-swatches";
import "vue-swatches/dist/vue-swatches.css";

import vMultiselectListbox from "vue-multiselect-listbox";
import "vue-multiselect-listbox/dist/vue-multi-select-listbox.css";

import NavigationTabs from "@/components/Customers/Customer/NavigationTabs";
import ViewLoader from "@/components/Common/ViewLoader";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import TextInput from "@/components/Common/Inputs/TextInput";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import SuccessButton from "@/components/Common/Buttons/SuccessButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import WarningButton from "@/components/Common/Buttons/WarningButton";
import CustomerActionsMobile from "@/components/Customers/Customer/CustomerActionsMobile";
import CustomerActionsDesktop from "@/components/Customers/Customer/CustomerActionsDesktop";
import MembershipLogo from "@/components/Customers/Customer/MembershipLogo";
import Error404 from "@/components/Common/Error404";

export default {
  name: "CustomerData",

  mixins: [FormErrorsMixin],

  components: {
    MembershipLogo,
    ViewLoader,
    AlertBox,
    NavigationTabs,
    TextInput,
    InfoButton,
    SuccessButton,
    DangerButton,
    WarningButton,
    CustomerActionsMobile,
    CustomerActionsDesktop,
    Multiselect,
    DatePicker,
    VSwatches,
    vMultiselectListbox,
    Error404
  },

  data() {
    return {
      countries: [],
      loading: false,
      updating: false,
      noDataExist: false,
      membershipStatus: null,
      customerBalance: 0,
      customerDeleted: false,
      customerLocked: false,
      customerUnconfirmed: false,
      customerInterested: false,
      exceedanceWarning: false,
      originalFirstName: null,
      originalLastName: null,
      selectedCountry: {
        states: null
      },
      addressCheckActive: false,
      addressVerified: false,
      isPortalUser: false,
      dateFormat: "DD.MM.YYYY",
      selectedCustomer: {
        first_name: null,
        last_name: null,
        gender: null,
        title: null,
        email: null,
        birthdate: null,
        color_code: null,
        note: null,
        position: null,
        number: null,
        phone_number: {
          prefix: null,
          number: null
        },
        address: {
          country_id: null,
          state_id: null,
          city: null,
          post_code: null,
          street_name: null,
          street_number: null,
          additional_info: null
        }
      },
      selectedCustomerId: null,
      validatedInputs: [
        "first_name",
        "last_name",
        "gender",
        "title",
        "email",
        "birthdate",
        "color_code",
        "note",
        "position",
        "number",
        "phone_number.prefix",
        "phone_number.number",
        "address.country_id",
        "address.state_id",
        "address.city",
        "address.post_code",
        "address.street_name",
        "address.street_number",
        "address.additional_info"
      ],
      paymentMethodValue: [
        //
      ],
      paymentMethodOptions: [
        { name: "Rechnung", id: 1 },
        { name: "Bar vor Ort", id: 2 },
        { name: "Bankomat vor Ort", id: 3 },
        { name: "Kreditkarte", id: 4 },
        { name: "Paypal", id: 5 }
      ],
      // groups data
      updatingGroupCustomers: false,
      allGroups: [],
      availableGroups: [],
      originalSelectedGroups: [],
      selectedGroups: [],
      // organizations data
      updatingOrganizationCustomers: false,
      allOrganizations: [],
      availableOrganizations: [],
      originalSelectedOrganizations: [],
      selectedOrganizations: []
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getGroups();
      vm.getOrganizations();
      vm.getCountries();
      vm.selectedCustomerId = vm.$route.params.customer_id;
      if (
        typeof vm.selectedCustomerId !== "undefined" &&
        vm.selectedCustomerId !== null
      ) {
        if (vm.$route.query.customer_created) {
          swal("Success!", "The customer was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
        vm.getCustomer(() => {
          //
        });
      }
    });
  },

  beforeRouteUpdate(to, from, next) {
    this.getGroups();
    this.getOrganizations();
    this.getCountries();
    if (
      typeof this.selectedCustomerId !== "undefined" &&
      this.selectedCustomerId !== null
    ) {
      this.getCustomer(() => {
        //
      });
    }
    next();
  },

  mounted: function() {
    //
  },

  methods: {
    addPaymentMethod(paymentMethod) {
      this.paymentMethodValue.push(paymentMethod);
    },

    restoreDefaultPaymentMethods() {
      this.paymentMethodValue = [];
    },

    parseBirthdate(val) {
      if (
        this.selectedCustomer.birthdate != null &&
        Object.prototype.toString.call(this.selectedCustomer.birthdate) ===
          "[object Date]"
      ) {
        let val = this.selectedCustomer.birthdate;
        this.selectedCustomer.birthdate =
          val.getFullYear() +
          "-" +
          (val.getMonth() + 1 > 9 ? "" : "0") +
          (val.getMonth() + 1) +
          "-" +
          (val.getDate() > 9 ? "" : "0") +
          val.getDate();
      } else if (this.selectedCustomer.birthdate != null && val != null) {
        let parts = this.selectedCustomer.birthdate.split("-");
        this.selectedCustomer.birthdate = new Date(
          parts[0],
          parts[1] - 1,
          parts[2]
        );
      } else if (this.selectedCustomer.birthdate != null && val == null) {
        let parts = this.selectedCustomer.birthdate.split(".");
        this.selectedCustomer.birthdate =
          parts[2] + "-" + parts[1] + "-" + parts[0];
      }
    },

    changeGroups() {
      this.updateCustomerGroups();
    },

    changeOrganizations() {
      this.updateCustomerOrganizations();
    },

    createOrUpdateCustomer() {
      if (this.selectedCustomerId == null) {
        this.createCustomer();
      } else {
        this.updateCustomer();
      }
    },

    createCustomer() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("customers");

      this.parseBirthdate();

      Api()
        .post(endpoint, this.selectedCustomer)
        .then(response => {
          this.selectedCustomerId = response.data.data.id;
          this.$router.push({
            name: "CustomerData",
            params: { customer_id: this.selectedCustomerId },
            query: { customer_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.$scrollTo("#customer-first-name", 500);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getCustomer(successCallback) {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "customers/" + this.selectedCustomerId
      );

      return Api()
        .get(endpoint)
        .then(response => {
          this.selectedCustomer = response.data.data;
          this.parseBirthdate(response.data.data.birthdate);
          this.originalFirstName = this.selectedCustomer.first_name;
          this.originalLastName = this.selectedCustomer.last_name;
          if (this.selectedCustomer.address.country_id != null) {
            this.countryShouldBeSelected({
              id: this.selectedCustomer.address.country_id
            });
            this.setSelectedCountry(this.selectedCustomer.address.country_id);
            if (this.selectedCustomer.address.state_id != null) {
              this.stateShouldBeSelected({
                id: this.selectedCustomer.address.state_id
              });
            }
          }
          if (typeof successCallback == "function") {
            successCallback();
          }
          this.getCustomerGroups();
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateCustomer() {
      if (this.loading || this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "customers/" + this.selectedCustomerId
      );

      let originalBirthdate = this.selectedCustomer.birthdate;
      this.parseBirthdate();

      Api()
        .put(endpoint, this.selectedCustomer)
        .then(response => {
          this.parseBirthdate(response.data.data.birthdate);
          this.getCustomer(() => {
            //
          });

          swal({
            title: "Success!",
            text: "The customer was updated.",
            icon: "success"
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
          this.selectedCustomer.birthdate = originalBirthdate;
        })
        .finally(() => {
          this.updating = false;
          this.$scrollTo("#customer-first-name", 500);
        });
    },

    getCountries(successCallback) {
      Api()
        .get("api/internal/v1/countries")
        .then(response => {
          this.countries = response.data.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    updateCustomerGroups() {
      if (this.originalSelectedGroups.length < this.selectedGroups.length) {
        for (let i = 0; i < this.selectedGroups.length; i++) {
          if (!this.originalSelectedGroups.includes(this.selectedGroups[i])) {
            this.addCustomerToGroup(this.selectedGroups[i].id);
          }
        }
      } else if (
        this.originalSelectedGroups.length > this.selectedGroups.length
      ) {
        for (let i = 0; i < this.originalSelectedGroups.length; i++) {
          if (!this.selectedGroups.includes(this.originalSelectedGroups[i])) {
            this.removeCustomerFromGroup(this.originalSelectedGroups[i].id);
            this.moveToAvailableGroups(this.originalSelectedGroups[i]);
          }
        }
      }

      this.originalSelectedGroups = this.selectedGroups;
    },

    addCustomerToGroup(selectedGroupId) {
      if (this.updatingGroupCustomers) {
        return;
      }

      this.resetErrors();
      this.updatingGroupCustomers = true;
      const endpoint = this.$partnerEndpoint(
        "groups/" + selectedGroupId + "/customers"
      );

      Api()
        .post(endpoint, { customer_id: this.selectedCustomerId })
        .then(() => {})
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updatingGroupCustomers = false;
        });
    },

    removeCustomerFromGroup(selectedGroupId) {
      if (this.updatingGroupCustomers) {
        return;
      }

      this.resetErrors();
      this.updatingGroupCustomers = true;
      const endpoint = this.$partnerEndpoint(
        "groups/" + selectedGroupId + "/customers/" + this.selectedCustomerId
      );

      Api()
        .delete(endpoint)
        .then(() => {})
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updatingGroupCustomers = false;
        });
    },

    getGroups(successCallback) {
      this.groups = [];
      const endpoint = this.$partnerEndpoint("groups");

      Api()
        .get(endpoint)
        .then(response => {
          this.allGroups = response.data.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        });
    },

    getCustomerGroups(successCallback) {
      this.resetErrors();
      let customerGroups = [];
      const endpoint = this.$partnerEndpoint(
        "customers/" + this.selectedCustomerId + "/groups"
      );

      Api()
        .get(endpoint)
        .then(response => {
          customerGroups = response.data.data;
          for (let i = 0; i < this.allGroups.length; i++) {
            let selected = false;
            for (let j = 0; j < customerGroups.length; j++) {
              if (this.allGroups[i].id == customerGroups[j].id) {
                selected = true;
                break;
              }
            }
            if (selected) {
              this.moveToSelectedGroups(this.allGroups[i]);
            } else {
              this.moveToAvailableGroups(this.allGroups[i]);
            }
          }

          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error.response);
        })
        .finally(() => {
          this.getCustomerOrganizations();
        });
    },

    moveToSelectedGroups(customerGroup) {
      if (!this.selectedGroups.includes(customerGroup)) {
        this.selectedGroups.push(customerGroup);
      }
      this.originalSelectedGroups = this.selectedGroups;
    },

    moveToAvailableGroups(customerGroup) {
      if (!this.availableGroups.includes(customerGroup)) {
        this.availableGroups.push(customerGroup);
      }
    },

    updateCustomerOrganizations() {
      if (
        this.originalSelectedOrganizations.length <
        this.selectedOrganizations.length
      ) {
        for (let i = 0; i < this.selectedOrganizations.length; i++) {
          if (
            !this.originalSelectedOrganizations.includes(
              this.selectedOrganizations[i]
            )
          ) {
            this.addCustomerToOrganization(this.selectedOrganizations[i].id);
          }
        }
      } else if (
        this.originalSelectedOrganizations.length >
        this.selectedOrganizations.length
      ) {
        for (let i = 0; i < this.originalSelectedOrganizations.length; i++) {
          if (
            !this.selectedOrganizations.includes(
              this.originalSelectedOrganizations[i]
            )
          ) {
            this.removeCustomerFromOrganization(
              this.originalSelectedOrganizations[i].id
            );
            this.moveToAvailableOrganizations(
              this.originalSelectedOrganizations[i]
            );
          }
        }
      }

      this.originalSelectedOrganizations = this.selectedOrganizations;
    },

    addCustomerToOrganization(selectedOrganizationId) {
      if (this.updatingOrganizationCustomers) {
        return;
      }

      this.resetErrors();
      this.updatingOrganizationCustomers = true;
      const endpoint = this.$partnerEndpoint(
        "organizations/" + selectedOrganizationId + "/customers"
      );

      Api()
        .post(endpoint, { customer_id: this.selectedCustomerId })
        .then(() => {})
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updatingOrganizationCustomers = false;
        });
    },

    removeCustomerFromOrganization(selectedOrganizationId) {
      if (this.updatingOrganizationCustomers) {
        return;
      }

      this.resetErrors();
      this.updatingOrganizationCustomers = true;
      const endpoint = this.$partnerEndpoint(
        "organizations/" +
          selectedOrganizationId +
          "/customers/" +
          this.selectedCustomerId
      );

      Api()
        .delete(endpoint)
        .then(() => {})
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updatingOrganizationCustomers = false;
        });
    },

    getOrganizations(successCallback) {
      this.organizations = [];
      const endpoint = this.$partnerEndpoint("organizations");

      Api()
        .get(endpoint)
        .then(response => {
          this.allOrganizations = response.data.data;
          if (typeof successCallback == "function") {
            successCallback();
          }
        });
    },

    getCustomerOrganizations(successCallback) {
      let customerOrganizations = [];
      const endpoint = this.$partnerEndpoint(
        "customers/" + this.selectedCustomerId + "/organizations"
      );
      this.resetErrors();

      Api()
        .get(endpoint)
        .then(response => {
          customerOrganizations = response.data.data;
          for (let i = 0; i < this.allOrganizations.length; i++) {
            let selected = false;
            for (let j = 0; j < customerOrganizations.length; j++) {
              if (this.allOrganizations[i].id == customerOrganizations[j].id) {
                selected = true;
                break;
              }
            }
            if (selected) {
              this.moveToSelectedOrganizations(this.allOrganizations[i]);
            } else {
              this.moveToAvailableOrganizations(this.allOrganizations[i]);
            }
          }

          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    moveToSelectedOrganizations(customerOrganization) {
      if (!this.selectedOrganizations.includes(customerOrganization)) {
        this.selectedOrganizations.push(customerOrganization);
      }
      this.originalSelectedOrganizations = this.selectedOrganizations;
    },

    moveToAvailableOrganizations(customerOrganization) {
      if (!this.availableOrganizations.includes(customerOrganization)) {
        this.availableOrganizations.push(customerOrganization);
      }
    },

    countryShouldBeSelected(country) {
      return this.selectedCustomer.address.country_id == country.id;
    },

    stateShouldBeSelected(state) {
      return this.selectedCustomer.address.state_id == state.id;
    },

    setSelectedCountry(countryId) {
      for (let i = 0; i < this.countries.length; i++) {
        if (countryId == this.countries[i].id) {
          this.selectedCountry = this.countries[i];
        }
      }
    },

    onChangeSelectedCountry(event) {
      for (let i = 0; i < this.countries.length; i++) {
        if (event.target.value == this.countries[i].id) {
          this.selectedCountry = this.countries[i];
        }
      }
    }
  },

  computed: {
    //
  },

  watch: {
    //
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-8 {
  margin-top: 1.3rem;
}

.cursor-pointer {
  cursor: pointer;
}

.color-preview {
  pointer-events: none !important;
  opacity: 1 !important;
}

.btn-action {
  background-color: pink !important;
}
</style>
